import { useEffect, useRef, useCallback } from "react";

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutos
const WARNING_BEFORE_MS = 1 * 60 * 1000; // 1 minuto antes de cerrar

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];

/**
 * Hook que cierra sesión automáticamente tras inactividad.
 * @param {Object} options
 * @param {Function} options.onLogout - Callback al cerrar sesión
 * @param {Function} [options.onWarning] - Callback 1 min antes de cerrar (para mostrar aviso)
 * @param {number} [options.timeoutMs] - Tiempo total de inactividad en ms (default: 15 min)
 * @param {number} [options.warningBeforeMs] - Cuánto antes del cierre mostrar aviso (default: 1 min)
 * @param {boolean} [options.enabled] - Si está activo (default: true)
 */
export function useInactivityLogout({
  onLogout,
  onWarning,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warningBeforeMs = WARNING_BEFORE_MS,
  enabled = true
}) {
  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef(null);
  const warningTimeoutIdRef = useRef(null);
  const warningShownRef = useRef(false);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningTimeoutIdRef.current) clearTimeout(warningTimeoutIdRef.current);

    if (!enabled || !onLogout) return;

    // Timer principal: al llegar al timeout, mostrar aviso o cerrar
    timeoutIdRef.current = setTimeout(() => {
      if (!warningShownRef.current && onWarning) {
        warningShownRef.current = true;
        onWarning();
        // Dar 1 minuto más antes de cerrar
        warningTimeoutIdRef.current = setTimeout(() => {
          onLogout();
        }, warningBeforeMs);
      } else {
        onLogout();
      }
    }, timeoutMs - warningBeforeMs);
  }, [enabled, onLogout, onWarning, timeoutMs, warningBeforeMs]);

  const handleActivity = useCallback(() => {
    if (!enabled) return;
    resetTimer();
  }, [enabled, resetTimer]);

  useEffect(() => {
    if (!enabled || !onLogout) return;

    resetTimer();

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, handleActivity);
    });
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") handleActivity();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, handleActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (warningTimeoutIdRef.current) clearTimeout(warningTimeoutIdRef.current);
    };
  }, [enabled, onLogout, handleActivity, resetTimer]);
}
